import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect, sortDropDownItems, type DeprecatedDropdownMenuOption } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentV2Resource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { ArgoCDEventDispatcher } from "~/analytics/Analytics";
import { SourceCardList } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/SourceCardList";
import type { ConfiguredSource, ConfiguredSources } from "../ConfiguredSource";
import { sharedStyles } from "../sharedStyles";
import { AnnotationHelpNote } from "./AnnotationHelpNote";
const styles = {
    annotationHelp: css({
        marginBottom: space[16],
    }),
};
type ScopingConfigurationViewProps = {
    index: number;
    configuredSources: ConfiguredSources;
    setConfiguredSources: (configuredSources: ConfiguredSources) => void;
    projects: ProjectResource[] | undefined;
    environments: EnvironmentV2Resource[] | undefined;
    tenants: TenantResource[] | undefined;
    isMultiSourceApplication: boolean;
    argoCDEventDispatcher: ArgoCDEventDispatcher;
};
export const ScopingConfigurationView = (props: ScopingConfigurationViewProps) => {
    const { index, configuredSources, setConfiguredSources, projects, environments, tenants, isMultiSourceApplication, argoCDEventDispatcher } = props;
    //We sort the project items here so when we retrieve the first one to set a default value, we get the same first item as in the select
    //Otherwise you get the first created project, which might be sorted into a different order in the drop down
    const projectItems: DeprecatedDropdownMenuOption[] = useMemo(() => sortDropDownItems((projects || []).map((p) => ({
        value: p.Slug,
        text: p.Name,
    }))), [projects]);
    const environmentItems: DeprecatedDropdownMenuOption[] = useMemo(() => sortBy(environments || [], (e) => e.SortOrder).map((e) => ({
        value: e.Slug,
        text: e.Name,
    })), [environments]);
    const tenantItems: DeprecatedDropdownMenuOption[] = useMemo(() => (tenants || []).map((t) => ({
        value: t.Slug,
        text: t.Name,
    })), [tenants]);
    const updateConfiguredSource = (key: string, updated: Partial<ConfiguredSource>) => {
        const updatedSources = { ...configuredSources };
        updatedSources[key] = {
            ...configuredSources[key],
            ...updated,
        };
        const keys = Object.keys(updated);
        if (keys.includes("projectSlug")) {
            argoCDEventDispatcher("Select Argo CD Generate Annotations Drawer Project");
        }
        if (keys.includes("environmentSlug")) {
            argoCDEventDispatcher("Select Argo CD Generate Annotations Drawer Environment");
        }
        if (keys.includes("tenantSlug")) {
            argoCDEventDispatcher("Select Argo CD Generate Annotations Drawer Tenant");
        }
        setConfiguredSources(updatedSources);
    };
    const selectedSources = useMemo(() => {
        const selectedEntries = Object.entries(configuredSources).filter(([, configuredSource]) => configuredSource.isSelectedForScoping);
        return Object.fromEntries(selectedEntries);
    }, [configuredSources]);
    const wrapIfEmpty = (children: ReactNode, count: number, itemName: string) => {
        if (count > 0) {
            return children;
        }
        return (<Tooltip content={`No ${itemName} available`} position={"right"}>
                {children}
            </Tooltip>);
    };
    return (<div>
            <h2 className={sharedStyles.header}>{index}. Configure scoping annotations</h2>
            <div className={styles.annotationHelp}>
                <p>These annotations map your Argo CD application to Octopus projects, environments and tenants.</p>
                <AnnotationHelpNote />
            </div>
            <SourceCardList configuredSources={selectedSources} isMultiSourceApplication={isMultiSourceApplication} cardContentRenderer={(key, configuredSource) => (<>
                        {wrapIfEmpty(<DeprecatedSelect label={`Project`} allowClear={false} items={projectItems} value={configuredSource.projectSlug} onChange={(val) => updateConfiguredSource(key, { projectSlug: val })} disabled={projectItems.length === 0} allowFilter={true} sortItems={false}/>, projectItems.length, "projects")}
                        {wrapIfEmpty(<DeprecatedSelect label={`Environment`} allowClear={false} items={environmentItems} value={configuredSource.environmentSlug} onChange={(val) => updateConfiguredSource(key, { environmentSlug: val })} disabled={environmentItems.length === 0} allowFilter={true} sortItems={false}/>, environmentItems.length, "environments")}
                        {wrapIfEmpty(<DeprecatedSelect label={"Tenant (optional)"} allowClear={true} items={tenantItems} value={configuredSource.tenantSlug} onChange={(val) => updateConfiguredSource(key, { tenantSlug: val })} disabled={tenantItems.length === 0} allowFilter={true}/>, tenantItems.length, "tenants")}
                    </>)}/>
        </div>);
};
